import { Component, ElementRef, Inject, Input, OnChanges, OnInit, Optional, SimpleChanges, ViewChild } from '@angular/core';
import { ModalService } from '../../../core/services/modal.service';
import { Base64UrlService } from '../../../services/base64url.service';
import { ExportFactoringBuntResponse, FactoringBuntGet, FactoringBuntService, GetFactoringBuntParams } from '@app/services/factoring-bunt.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';

@Component({
    selector: 'tt-factoring-bunt',
    templateUrl: './factoring-bunt.component.html',
    styleUrls: ['./factoring-bunt.component.css'],
})
export class FactoringBuntComponent implements OnInit, OnChanges {
    @Input()
    public params?: GetFactoringBuntParams;

    public page?: FactoringBuntGet;

    public saving = false;

    public creatingFile = false;

    @ViewChild('export')
    public exportAnchor?: ElementRef<HTMLAnchorElement>;

    constructor(
        private factoringBunt: FactoringBuntService,
        private modal: ModalService,
        private base64: Base64UrlService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public async saveChanges() {
        if (!this.page) return;

        try {
            this.saving = true;

            const { errorcode, errormessage } = await this.factoringBunt.saveFactoringBunt(this.page);

            if (errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.navigator?.back();
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.saving = false;
        }
    }

    public async createFile() {
        if (!this.page) return;

        try {
            this.creatingFile = true;

            const response = await this.factoringBunt.exportFactoringBunt(this.page);

            if (response?.errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: response.errormessage });
            } else {
                const files = this.getAllExportedFiles(response);

                for (let file of files) {
                    this.downloadFile(file);
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.creatingFile = false;
        }
    }

    private getAllExportedFiles(response: ExportFactoringBuntResponse): { filename: string; filecontent: string }[] {
        return Object.entries(response)
            .filter(([key]) => /^filename\d*$/.test(key))
            .map(([key, filename]) => ({ filename, filecontent: response[key.replace('filename', 'filecontent')] }))
            .filter((file) => (file.filecontent ?? '') > '' || (file.filename ?? '') > '');
    }

    private downloadFile(params: { filecontent: string; filename: string }) {
        if (!this.exportAnchor?.nativeElement) return;
        const anchor = this.exportAnchor.nativeElement;

        const text = this.base64.urlDecode(params.filecontent);

        const textData = new Blob([text], { type: 'text/txt' });
        const textUrl = URL.createObjectURL(textData);

        anchor.href = textUrl;
        anchor.download = params.filename;
        anchor.click();
    }

    private async loadPage() {
        if (!!this.params) {
            this.page = await this.factoringBunt.getFactoringBunt(this.params);
        }
    }

    private async init() {
        this.loadPage();
    }

    ngOnInit() {
        if (!!this.navigator) {
            this.navigator.setSize?.({ width: '80rem' });
            this.navigator.setHeading?.('p2_m_factoringbunt', true);
            this.params = this.navigator.params as any;
            this.init();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this.init();
        }
    }
}
